import {
  getPermanentPurchaseDefinition,
  getPermanentPurchaseNextCost,
  getRewardCodeDefinition,
  isRewardCodeId,
} from "@/features/payment/paymentDefinitions";
import type {
  BundleEntitlementKey,
  HoverIdlePaymentState,
  PermanentPurchaseId,
} from "@/features/payment/types";

export type EntitlementGrantDecision =
  | { readonly ok: true; readonly nextPayment: HoverIdlePaymentState }
  | { readonly ok: false; readonly reason: "already-granted" };

export type RewardCodeRedeemDecision =
  | { readonly ok: true; readonly nextPayment: HoverIdlePaymentState }
  | {
      readonly ok: false;
      readonly reason: "invalid-code" | "already-redeemed";
    };

export type PermanentItemPurchaseDecision =
  | { readonly ok: true; readonly nextPayment: HoverIdlePaymentState }
  | {
      readonly ok: false;
      readonly reason: "max-reached" | "insufficient-token";
    };

export function resolveEntitlementGrant(
  payment: HoverIdlePaymentState,
  key: BundleEntitlementKey,
): EntitlementGrantDecision {
  if (payment.entitlements.includes(key)) {
    return { ok: false, reason: "already-granted" };
  }
  return {
    ok: true,
    nextPayment: {
      ...payment,
      entitlements: [...payment.entitlements, key],
    },
  };
}

export function resolveEntitlementClaim(
  payment: HoverIdlePaymentState,
  key: BundleEntitlementKey,
): HoverIdlePaymentState | null {
  if (!payment.entitlements.includes(key)) {
    return null;
  }
  if (payment.claimedEntitlements.includes(key)) {
    return null;
  }
  return {
    ...payment,
    claimedEntitlements: [...payment.claimedEntitlements, key],
  };
}

export function resolveRewardCodeRedeem(
  payment: HoverIdlePaymentState,
  code: string,
): RewardCodeRedeemDecision {
  const normalizedCode = code.trim();
  if (!isRewardCodeId(normalizedCode)) {
    return { ok: false, reason: "invalid-code" };
  }
  if (payment.redeemedRewardCodes.includes(normalizedCode)) {
    return { ok: false, reason: "already-redeemed" };
  }
  const definition = getRewardCodeDefinition(normalizedCode);
  return {
    ok: true,
    nextPayment: {
      ...payment,
      token: payment.token + definition.rewardToken,
      redeemedRewardCodes: [...payment.redeemedRewardCodes, normalizedCode],
    },
  };
}

export function resolvePermanentItemPurchase(
  payment: HoverIdlePaymentState,
  id: PermanentPurchaseId,
): PermanentItemPurchaseDecision {
  const definition = getPermanentPurchaseDefinition(id);
  const currentCount = payment.purchasedPermanentItems[id];
  if (currentCount >= definition.maxPurchases) {
    return { ok: false, reason: "max-reached" };
  }
  const nextCost = getPermanentPurchaseNextCost(definition, currentCount);
  if (payment.token < nextCost) {
    return { ok: false, reason: "insufficient-token" };
  }
  return {
    ok: true,
    nextPayment: {
      ...payment,
      token: payment.token - nextCost,
      purchasedPermanentItems: {
        ...payment.purchasedPermanentItems,
        [id]: currentCount + 1,
      },
    },
  };
}

export function withPermanentItemGrantedFromBundle(
  payment: HoverIdlePaymentState,
  id: PermanentPurchaseId,
): HoverIdlePaymentState {
  return {
    ...payment,
    purchasedPermanentItems: {
      ...payment.purchasedPermanentItems,
      [id]: payment.purchasedPermanentItems[id] + 1,
    },
  };
}
